import { useEffect, useState } from "react";
import { Car, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { apiFetch, API } from "@/portal/api";
import { toast } from "sonner";

const AttachToVehicleDialog = ({ open, onClose, photoIds, token, onDone }) => {
  const [vehicles, setVehicles] = useState(null);
  const [label, setLabel] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setVehicles(null);
    apiFetch("/admin/vehicles", { token }).then(setVehicles).catch((e) => { toast.error(e.message); setVehicles([]); });
  }, [open, token]);

  const attach = async (v) => {
    setBusyId(v.id);
    try {
      const r = await apiFetch(`/admin/vehicles/${v.id}/images/from-photos`, { method: "POST", token, body: { photo_ids: photoIds, label } });
      toast.success(`${r.added.length} ${r.added.length === 1 ? "fotka vložena" : "fotky vloženy"} k vozu ${v.name}.${r.failed ? ` Nepodařilo se: ${r.failed}.` : ""}`);
      onDone?.(v);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f11] border-white/10 text-white sm:max-w-md" data-testid="attach-vehicle-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Cabinet_Grotesk']">Vložit k vozu na webu</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {photoIds.length === 1 ? "Fotka se přenese" : `${photoIds.length} fotek se přenese`} přímo na server v plné kvalitě – bez stahování a nahrávání.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="attach-vehicle-preview">
          {photoIds.slice(0, 6).map((id) => <img key={id} src={`${API}/admin/photos/${id}/file`} alt="" className="h-20 rounded-lg border border-white/10 object-contain bg-black/40 shrink-0" />)}
          {photoIds.length > 6 && <span className="self-center text-xs text-zinc-500">+{photoIds.length - 6}</span>}
        </div>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Popisek fotky (nepovinné, např. Zepředu)"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500"
          data-testid="attach-vehicle-label" />
        <div className="max-h-72 overflow-y-auto space-y-1.5 -mx-1 px-1" data-testid="attach-vehicle-list">
          {vehicles === null ? (
            <div className="py-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : vehicles.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-500">Zatím nemáte žádný vůz na webu. Vytvořte ho v záložce „Vozy na webu".</p>
          ) : vehicles.map((v) => (
            <button key={v.id} onClick={() => attach(v)} disabled={!!busyId}
              className="w-full flex items-center gap-3 rounded-xl border border-white/10 hover:border-orange-500/60 hover:bg-white/5 px-3 py-2 text-left transition-colors disabled:opacity-60"
              data-testid={`attach-vehicle-${v.id}`}>
              <div className="w-12 h-9 rounded-md overflow-hidden bg-black/40 shrink-0 flex items-center justify-center">
                {v.images?.[0]?.url ? <img src={`${v.images[0].url.startsWith("/api") ? process.env.REACT_APP_BACKEND_URL : ""}${v.images[0].url}${v.images[0].url.startsWith("/api") ? "?v=thumb" : ""}`} alt="" className="w-full h-full object-cover" /> : <Car className="w-4 h-4 text-zinc-600" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{v.name}</p>
                <p className="text-[11px] text-zinc-500">{v.images?.length || 0} fotek · {v.published ? "publikováno" : "skryto"}</p>
              </div>
              {busyId === v.id ? <Loader2 className="w-4 h-4 animate-spin text-orange-400" /> : <CheckCircle2 className="w-4 h-4 text-zinc-600" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachToVehicleDialog;
